import { GitBranch, FileText, FilePlus, FileX, FileEdit, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/ui/status-dot";
import { cn } from "@/lib/utils";

interface GitChange {
  status: string;
  path: string;
}

interface GitStatusData {
  branch: string;
  clean: boolean;
  changes: GitChange[];
  ahead: number;
  behind: number;
}

interface GitStatusWidgetProps {
  data: GitStatusData;
  actions?: Array<{ id: string; label: string; style?: string }>;
  onAction?: (actionId: string) => void;
}

const statusIcon: Record<string, typeof FileText> = {
  M:  FileEdit,
  A:  FilePlus,
  D:  FileX,
  "?": FilePlus,
  "??": FilePlus,
};

const statusColor: Record<string, string> = {
  M:  "text-intri-warning",
  A:  "text-intri-success",
  D:  "text-intri-error",
  "?": "text-intri-dim",
  "??": "text-intri-dim",
};

const statusLabel: Record<string, string> = {
  M:  "modified",
  A:  "added",
  D:  "deleted",
  "?": "untracked",
  "??": "untracked",
};

export function GitStatusWidget({ data, actions, onAction }: GitStatusWidgetProps) {
  const { branch = "unknown", clean = true, changes = [], ahead = 0, behind = 0 } = data ?? {};

  return (
    <Card variant="default" padding="none" className="overflow-hidden max-w-md animate-slide-up">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-intri-accent-ghost">
          <GitBranch size={16} className="text-intri-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-intri-text truncate">{branch}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusDot
              status={clean ? "connected" : "warning"}
              size="sm"
              label={clean ? "Clean" : `${changes.length} change${changes.length !== 1 ? "s" : ""}`}
              labelHidden={false}
            />
            {ahead > 0 && (
              <Badge variant="accent" size="sm">{ahead} ahead</Badge>
            )}
            {behind > 0 && (
              <Badge variant="warning" size="sm">{behind} behind</Badge>
            )}
          </div>
        </div>
        {clean && (
          <Check size={18} className="text-intri-success shrink-0" />
        )}
      </div>

      {/* File list */}
      {changes.length > 0 && (
        <CardContent className="px-0 border-t border-intri-border">
          <ul className="divide-y divide-intri-border">
            {changes.slice(0, 10).map((change, i) => {
              const Icon = statusIcon[change.status] ?? FileText;
              const color = statusColor[change.status] ?? "text-intri-dim";
              const label = statusLabel[change.status] ?? change.status;

              return (
                <li key={i} className="flex items-center gap-2 px-4 py-2 text-sm">
                  <Icon size={14} className={cn("shrink-0", color)} />
                  <span className="flex-1 font-mono text-xs text-intri-text truncate">
                    {change.path}
                  </span>
                  <span className={cn("text-2xs", color)}>{label}</span>
                </li>
              );
            })}
            {changes.length > 10 && (
              <li className="px-4 py-2 text-2xs text-intri-muted text-center">
                +{changes.length - 10} more files
              </li>
            )}
          </ul>
        </CardContent>
      )}

      {/* Actions */}
      {actions && actions.length > 0 && (
        <div className="flex gap-2 px-4 py-3 border-t border-intri-border">
          {actions.map((action) => (
            <Button
              key={action.id}
              variant={action.style === "primary" ? "default" : "secondary"}
              size="sm"
              onClick={() => onAction?.(action.id)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </Card>
  );
}
